import { Link } from "react-router-dom";
import {
  ArrowRight,
  Phone,
  ClipboardList,
  MessageCircle,
  LineChart,
  Brain,
  BellRing,
  ShieldCheck,
  Send,
} from "lucide-react";
import Navbar from "@/components/Navbar";
import Footer from "@/components/Footer";

const HERO_BG_GRADIENT =
  "radial-gradient(ellipse at 50% 30%, rgba(70,70,70,0.35) 0%, rgba(10,10,10,0.95) 55%, #000 100%)";

const INCLUSIONS = [
  {
    icon: Phone,
    title: "Weekly 1-on-1 Calls",
    desc: "A private 60-minute video session with your mentor every week. Screen-share your trades, ask anything, walk away with clear moves for the next 7 days.",
  },
  {
    icon: ClipboardList,
    title: "Personal Trading Plan",
    desc: "Built around your capital, risk tolerance, time zone and lifestyle. No copy-paste blueprints — every plan is written for you.",
  },
  {
    icon: MessageCircle,
    title: "Direct Line, 7 Days a Week",
    desc: "A private WhatsApp / Telegram thread with your mentor. Send a chart at 2am, get eyes on it. This is what \"unlimited access\" actually means.",
  },
  {
    icon: LineChart,
    title: "Live Trade Reviews",
    desc: "Post entries, exits and mid-trade doubts — receive a full breakdown of what worked, what didn't, and exactly how to sharpen the setup next time.",
  },
  {
    icon: Brain,
    title: "Psychology & Discipline Coaching",
    desc: "The mental game is 80% of the P&L. Sessions cover risk management, tilt control, position sizing, journaling — the stuff nobody else teaches.",
  },
  {
    icon: BellRing,
    title: "Priority Signal Reception",
    desc: "You see the plays the moment they're formed — before they hit the wider group room. First in on every call, every session.",
  },
];

const IDEAL = [
  "Serious traders with £5,000+ capital ready to scale, not learn from zero",
  "Members already inside the group rooms who want the next level of access",
  "Anyone hunting six-figure years and needs an operator in their corner",
];

const PROCESS = [
  { n: "01", title: "Book", desc: "Grab a slot on Issy's calendar — the button below opens the booking page directly. Pick a time that suits, drop your details, done." },
  { n: "02", title: "Discovery Call", desc: "30-minute conversation to see if we're a fit. Two-way vetting — mentorship isn't for everyone." },
  { n: "03", title: "Onboarding", desc: "Your personal plan is built, mentor is assigned, weekly slot is booked, and your private line is opened." },
  { n: "04", title: "The Work", desc: "Weekly 1-1s, live reviews, direct-line access, and a mentor watching your back every session. Results follow." },
];

export default function Mentorship() {
  return (
    <div className="relative min-h-screen bg-black text-white overflow-x-hidden grain" data-testid="mentorship-page">
      <Navbar />

      {/* HERO */}
      <section
        className="relative min-h-[52vh] flex flex-col items-center justify-center px-6 pt-36 pb-14"
        data-testid="mentorship-hero"
      >
        <div className="absolute inset-0 opacity-95" style={{ background: HERO_BG_GRADIENT }} />
        <div className="absolute inset-0 bg-gradient-to-b from-black/50 via-black/70 to-black" />
        <div className="absolute inset-0 dot-pattern opacity-30" />

        <div className="relative z-10 text-center max-w-3xl">
          <p className="font-mono-serif text-[10px] md:text-[11px] tracking-[0.45em] text-white/45 fade-up delay-1">
            01-ON-01  //  BY APPLICATION
          </p>
          <h1 className="mt-5 font-display text-5xl md:text-7xl lg:text-8xl tracking-tight fade-up delay-2">
            Mentorship
          </h1>
          <p className="mt-6 text-white/60 max-w-2xl mx-auto text-sm md:text-base leading-relaxed fade-up delay-3">
            The group rooms are the pack. Mentorship is your own private operator —
            a direct line, a bespoke plan, and a mentor sitting in your corner every single week.
          </p>
        </div>
      </section>

      {/* INCLUSIONS */}
      <section className="relative z-10 px-4 md:px-6 mt-8 md:mt-12">
        <div className="max-w-6xl mx-auto">
          <p className="font-mono-serif text-[10px] tracking-[0.4em] text-white/45">01 // WHAT'S INCLUDED</p>
          <h2 className="mt-2 font-display text-3xl md:text-5xl">Every mentorship includes</h2>
          <p className="mt-4 text-white/55 max-w-2xl">
            Six pillars that turn a serious trader into a consistently profitable one. Every seat gets all of it — nothing is bolt-on or upsold later.
          </p>

          <div className="mt-10 grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-5 md:gap-6">
            {INCLUSIONS.map((it, i) => (
              <div
                key={it.title}
                data-testid={`inclusion-${i}`}
                className="frame p-6 md:p-7 hover:border-white/20 transition"
              >
                <div className="h-10 w-10 rounded-full border border-white/10 bg-white/5 flex items-center justify-center">
                  <it.icon size={16} className="text-white/85" />
                </div>
                <h3 className="mt-5 font-mono-serif text-[11px] tracking-[0.28em] text-white">
                  {it.title.toUpperCase()}
                </h3>
                <p className="mt-3 text-white/60 text-sm leading-relaxed">
                  {it.desc}
                </p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* WHO IT'S FOR */}
      <section className="relative z-10 px-4 md:px-6 mt-20 md:mt-28">
        <div className="max-w-6xl mx-auto grid grid-cols-1 lg:grid-cols-2 gap-10 items-start">
          <div>
            <p className="font-mono-serif text-[10px] tracking-[0.4em] text-white/45">02 // WHO IT'S FOR</p>
            <h2 className="mt-2 font-display text-3xl md:text-5xl">Not for everyone — and that's the point.</h2>
            <p className="mt-4 text-white/55">
              Mentorship is capped. We only take on traders we know we can move the needle for.
              If any of these describe you, apply.
            </p>
          </div>
          <ul className="space-y-4">
            {IDEAL.map((line, i) => (
              <li
                key={i}
                data-testid={`ideal-${i}`}
                className="frame p-5 md:p-6 flex items-start gap-4"
              >
                <ShieldCheck size={18} className="text-white/85 mt-0.5 shrink-0" />
                <span className="text-white/80 text-sm md:text-[15px] leading-relaxed">{line}</span>
              </li>
            ))}
          </ul>
        </div>
      </section>

      {/* PROCESS */}
      <section className="relative z-10 px-4 md:px-6 mt-20 md:mt-28">
        <div className="max-w-6xl mx-auto">
          <p className="font-mono-serif text-[10px] tracking-[0.4em] text-white/45">03 // THE PROCESS</p>
          <h2 className="mt-2 font-display text-3xl md:text-5xl">From apply to first call</h2>

          <div className="mt-10 grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-5 md:gap-6">
            {PROCESS.map((step) => (
              <div
                key={step.n}
                data-testid={`process-${step.n}`}
                className="frame p-6 md:p-7 relative overflow-hidden"
              >
                <span className="absolute right-4 top-4 font-mono-serif text-[10px] tracking-[0.3em] text-white/30">
                  {step.n}
                </span>
                <h3 className="font-display text-2xl md:text-3xl mt-2">{step.title}</h3>
                <p className="mt-4 text-white/60 text-sm leading-relaxed">{step.desc}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* CTA */}
      <section className="relative z-10 px-4 md:px-6 mt-24 md:mt-32 pb-24">
        <div className="max-w-3xl mx-auto text-center frame p-10 md:p-14">
          <p className="font-mono-serif text-[10px] tracking-[0.4em] text-white/45">SEATS LIMITED</p>
          <h2 className="mt-3 font-display text-3xl md:text-5xl">Ready to work with an operator?</h2>
          <p className="mt-4 text-white/60 max-w-xl mx-auto">
            Mentorship is application-only and capped so every trader gets real attention.
            Book a discovery call on Issy's calendar and we'll take it from there.
          </p>
          <a
            href="https://calendly.com/issyflash/talk-to-the-syndicate"
            target="_blank"
            rel="noopener noreferrer"
            data-testid="mentorship-apply-btn"
            className="mt-8 inline-flex items-center gap-3 btn-shimmer text-black font-mono-serif text-xs tracking-[0.3em] px-8 py-4 rounded-full hover:opacity-95 transition"
          >
            <Send size={14} /> APPLY FOR MENTORSHIP <ArrowRight size={16} />
          </a>
          <p className="mt-6 font-mono-serif text-[10px] tracking-[0.35em] text-white/40">
            BOOK YOUR DISCOVERY CALL  →  calendly.com/issyflash
          </p>
        </div>
      </section>

      <Footer />
    </div>
  );
}
